import React from "react";

/**
 * Kontrolka widoczności warstw na mapie — unified lista built-in + custom
 * WMS z drag-and-drop, renderowana wewnątrz FloatingPanelu rodzica
 * (TenantPublicPage / EditProcedurePage). Nagłówek i zwijanie pochodzą
 * z FloatingPanelu.
 *
 * Warstwy zarządzane przez OlMap.tsx, ale stan widoczności trzyma rodzic
 * (TenantPublicPage), żeby można go zapisać do localStorage i przywrócić
 * po odświeżeniu strony.
 *
 * Kolejność warstw od DÓŁ mapy (pierwsza wyświetlana pod spodem) do GÓRY
 * (ostatnia rysowana na wierzchu) — odpowiada kolejności w `layers: []`
 * przy tworzeniu mapy w OlMap.tsx.
 */

export type LayerId =
  | "osm"
  | "ortho"
  | "cieniowanie" // WMTS GUGIK NMT ShadedRelief — pod ortofotomapą
  | "mpzp"
  | "studium"
  | "dzialki"
  | "plan"
  | "areas"
  | "budynki"
  | "areaLabels"
  | "raster";      // COG raster (MPZP projekt) — wgrany przez admina

export type LayerVisibilityState = Record<LayerId, boolean>;

/**
 * Klucze warstw dla których dostępny jest suwak przezroczystości.
 * (`areas` i `areaLabels` to opcje renderingu w warstwie `plan`, nie osobne
 * warstwy — pomijamy w opacity.)
 */
export type OpacityLayerId = "osm" | "ortho" | "cieniowanie" | "mpzp" | "studium" | "dzialki" | "plan" | "budynki" | "raster";

export type LayerOpacityState = Record<OpacityLayerId, number>;

export const DEFAULT_LAYER_VISIBILITY: LayerVisibilityState = {
  osm: false,
  ortho: true,
  cieniowanie: false,
  mpzp: false,
  studium: false,
  dzialki: true,
  plan: true,
  areas: true,
  budynki: false,
  areaLabels: false,
  raster: true,
};

export const DEFAULT_LAYER_OPACITY: LayerOpacityState = {
  osm: 1.0,
  ortho: 1.0,
  cieniowanie: 1.0,
  mpzp: 0.7,
  studium: 0.7,
  dzialki: 1.0,
  plan: 0.85,
  budynki: 1.0,
  raster: 0.85,
};

/**
 * Lista warstw wyświetlanych w kontrolce — kolejność od góry do dołu
 * w UI (czyli najpierw te na wierzchu mapy, potem te pod spodem).
 * Etykieta widoczna w UI; ID odpowiada kluczowi w LayerVisibilityState.
 */
/**
 * Lista warstw w kolejności od góry do dołu w UI. Pole `opacityKey` wskazuje
 * którą wartość z LayerOpacityState użyć dla suwaka (undefined = bez suwaka,
 * np. dla opcji `areas`/`areaLabels` które są częścią warstwy `plan`).
 */
/** Lista wbudowanych warstw z meta-danymi — używana wewnętrznie i
 *  eksportowana do admin UI (EditProcedurePage: "Warstwy podstawowe"
 *  w dialogu dodawania). */
export const LAYERS: Array<{
  id: LayerId;
  label: string;
  description?: string;
  opacityKey?: OpacityLayerId;
  /** Pole-dziecko — renderowane z wcięciem pod rodzicem (np. areas/areaLabels pod plan). */
  parentOf?: LayerId;
}> = [
  { id: "plan", label: "Plan Ogólny — strefy", description: "Strefy planistyczne z GML", opacityKey: "plan" },
  { id: "areas", label: "Obszary specjalne (POG/OUZ/OZS/OSD)", parentOf: "plan" },
  { id: "areaLabels", label: "Etykiety stref specjalnych", parentOf: "plan" },
  { id: "raster", label: "Projekt MPZP", description: "Rysunek planu (raster)", opacityKey: "raster" },
  { id: "dzialki", label: "Granice działek", description: "WMS KIEG GUGIK (ewidencja gruntów z powiatów)", opacityKey: "dzialki" },
  { id: "budynki", label: "Budynki + numery działek", description: "WMS KIEG — od skali ~1:3000", opacityKey: "budynki" },
  { id: "studium", label: "Studium kierunków (WMS)", opacityKey: "studium" },
  { id: "mpzp", label: "MPZP — plany miejscowe (WMS)", opacityKey: "mpzp" },
  { id: "ortho", label: "Ortofotomapa (zdjęcia)", opacityKey: "ortho" },
  { id: "cieniowanie", label: "Cieniowanie terenu (NMT)", description: "WMTS GUGIK — widoczne po wyłączeniu ortofotomapy lub zmniejszeniu jej krycia", opacityKey: "cieniowanie" },
  { id: "osm", label: "OpenStreetMap", opacityKey: "osm" },
];

export type LayerControlProps = {
  visibility: LayerVisibilityState;
  onChange: (next: LayerVisibilityState) => void;
  opacity?: LayerOpacityState;
  onOpacityChange?: (next: LayerOpacityState) => void;
  /** Niestandardowe warstwy WMS dodane przez usera (AddWmsDialog). */
  customLayers?: import("./AddWmsDialog").CustomWmsLayer[];
  /** Zmiana stanu customLayers (toggle/opacity/reorder/delete). */
  onCustomLayersChange?: (next: import("./AddWmsDialog").CustomWmsLayer[]) => void;
  /** Klik w przycisk '+ Dodaj WMS' — rodzic otwiera AddWmsDialog. */
  onOpenAddWms?: () => void;
  /** Filtr dostępnych warstw — gdy podany, pokazujemy tylko te ID. */
  visibleLayerIds?: LayerId[];
  /** Etykiety warstw nadpisywane per-instance (np. "Projekt MPZP" z meta rastra). */
  layerLabels?: Partial<Record<LayerId, string>>;
  /**
   * Kolejność warstw od dołu (pierwsza na dnie mapy) do góry (ostatnia na
   * wierzchu). Elementy to `LayerId` dla built-in (bez `areas`/`areaLabels`
   * — to są dzieci warstwy `plan`) oraz `custom:${id}` dla custom WMS.
   * Undefined = domyślna kolejność z LAYERS const.
   */
  layerOrder?: string[];
  onLayerOrderChange?: (next: string[]) => void;
  /**
   * Gdy ustawione, przy KAŻDEJ warstwie (również built-in) pojawia się
   * przycisk ✕ usuwający ją z listy. Używane w admin UI (EditProcedurePage)
   * — tam dla built-in removal dodaje ID do `hiddenBuiltinLayerIds` w payload,
   * dla custom WMS usuwa z `customLayers`.
   */
  onRemoveBuiltinLayer?: (id: LayerId) => void;
  /** Etykieta przycisku "+ Dodaj WMS" — domyślnie "+ Dodaj WMS". W admin UI
   *  nadpisujemy na "+ Dodaj warstwę" (szerszy scope: WMS + built-in). */
  addWmsButtonLabel?: string;
};

function LayerControl({ visibility, onChange, opacity, onOpacityChange, customLayers, onCustomLayersChange, onOpenAddWms, visibleLayerIds, layerLabels, layerOrder, onLayerOrderChange, onRemoveBuiltinLayer, addWmsButtonLabel }: LayerControlProps) {
  // Zbiór LayerId dla których slider opacity jest aktualnie rozwinięty
  // (ikonka 🎚 klikana = toggle w tym Set). Local state — user może mieć
  // wiele paneli open naraz.
  const [expandedOpacity, setExpandedOpacity] = React.useState<Set<LayerId>>(new Set());

  const toggleOpacityPanel = (id: LayerId) => {
    setExpandedOpacity((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Unified lista built-in + custom WMS, wszystkie drag-and-drop. Nagłówek
  // "Warstwy mapy" + chevron ▾ pochodzą z FloatingPanelu dookoła, więc tu
  // żadnego nagłówka ani logiki zwijania. Dzieci (areas/areaLabels)
  // renderujemy pod warstwą rodzica (plan) z wcięciem — nie są osobno
  // drag-able, bo to opcje renderingu tej samej warstwy wektorowej.
  return (
    <EmbeddedLayerList
      visibility={visibility}
      onChange={onChange}
      opacity={opacity}
      onOpacityChange={onOpacityChange}
      customLayers={customLayers}
      onCustomLayersChange={onCustomLayersChange}
      onOpenAddWms={onOpenAddWms}
      visibleLayerIds={visibleLayerIds}
      layerLabels={layerLabels}
      expandedOpacity={expandedOpacity}
      onToggleOpacityPanel={toggleOpacityPanel}
      layerOrder={layerOrder}
      onLayerOrderChange={onLayerOrderChange}
      onRemoveBuiltinLayer={onRemoveBuiltinLayer}
      addWmsButtonLabel={addWmsButtonLabel}
    />
  );
}

const embeddedContainerStyle: React.CSSProperties = {
  background: "transparent",
  fontFamily: "system-ui, -apple-system, sans-serif",
  fontSize: 12,
  minWidth: 200,
  overflow: "auto",
};

const listStyle: React.CSSProperties = {
  margin: 0,
  padding: "4px 0",
  listStyle: "none",
};

const itemStyle: React.CSSProperties = {
  padding: "3px 10px",
};

const labelStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  userSelect: "none",
  flex: 1,
  // minWidth: 0 — niezbędne dla flex children żeby `overflow: hidden` +
  // `text-overflow: ellipsis` wewnątrz faktycznie zadziałały (bez tego
  // flex-container domyślnie ma min-content size).
  minWidth: 0,
};

const descStyle: React.CSSProperties = {
  color: "#6b7280",
  fontSize: 11,
  marginLeft: 4,
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
};

const iconBtnStyle: React.CSSProperties = {
  padding: "4px 8px",
  border: "1px solid transparent",
  borderRadius: 4,
  background: "transparent",
  cursor: "pointer",
  fontSize: 16,
  lineHeight: 1,
  flexShrink: 0,
  minWidth: 28,
  minHeight: 28,
};

const sliderRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 4px 2px 22px",
  fontSize: 10,
};

// ─────────────────────────────────────────────────────────────────────────
// EmbeddedLayerList — zunifikowana lista built-in + custom WMS z drag-drop.
// Używana tylko w trybie embedded (FloatingPanel w TenantPublicPage).
// ─────────────────────────────────────────────────────────────────────────

type EmbeddedLayerListProps = {
  visibility: LayerVisibilityState;
  onChange: (next: LayerVisibilityState) => void;
  opacity?: LayerOpacityState;
  onOpacityChange?: (next: LayerOpacityState) => void;
  customLayers?: import("./AddWmsDialog").CustomWmsLayer[];
  onCustomLayersChange?: (next: import("./AddWmsDialog").CustomWmsLayer[]) => void;
  onOpenAddWms?: () => void;
  visibleLayerIds?: LayerId[];
  layerLabels?: Partial<Record<LayerId, string>>;
  expandedOpacity: Set<LayerId>;
  onToggleOpacityPanel: (id: LayerId) => void;
  layerOrder?: string[];
  onLayerOrderChange?: (next: string[]) => void;
  onRemoveBuiltinLayer?: (id: LayerId) => void;
  addWmsButtonLabel?: string;
};

/** Prefix dla custom WMS w `layerOrder` — żeby ID-ki built-in (np. "plan")
 *  nie kolidowały z UUID-em customa. */
export const CUSTOM_LAYER_PREFIX = "custom:";

/** Dopisuje do zapisanej kolejności wbudowane warstwy, których w niej nie
 *  ma (np. warstwa dodana w nowszej wersji aplikacji), w ich domyślnym
 *  miejscu — zaraz pod najbliższą warstwą, która w domyślnej kolejności jest
 *  nad nimi — zamiast na sam dół listy. */
export function insertMissingBuiltinLayers(order: string[], defaultBuiltinOrder: string[]): string[] {
  const out = [...order];
  defaultBuiltinOrder.forEach((id, idx) => {
    if (out.includes(id)) return;
    let insertAt = 0;
    for (let j = idx - 1; j >= 0; j--) {
      const pos = out.indexOf(defaultBuiltinOrder[j]);
      if (pos !== -1) {
        insertAt = pos + 1;
        break;
      }
    }
    out.splice(insertAt, 0, id);
  });
  return out;
}

/** Oblicza kolejność do renderowania: najpierw te z `layerOrder` (w tej
 *  kolejności), potem dopisane nowe warstwy które jeszcze nie są w order
 *  (np. świeżo dodany custom WMS). Filtruje items które nie istnieją już
 *  w obecnym stanie (np. usunięty custom). */
function resolveRenderOrder(
  builtinIds: LayerId[],
  customIds: string[],
  order: string[] | undefined,
): string[] {
  const allIds: string[] = [
    ...builtinIds,
    ...customIds.map((id) => `${CUSTOM_LAYER_PREFIX}${id}`),
  ];
  if (!order) return allIds;
  order = insertMissingBuiltinLayers(order, builtinIds);
  const orderedSet = new Set(order);
  const existing = new Set(allIds);
  const out: string[] = [];
  const seen = new Set<string>();
  // Najpierw items z order (w kolejności), filtrując te które już nie
  // istnieją (usunięte custom WMS) i duplikaty — `order` z localStorage
  // mógł zostać zapisany przez starszą wersję, w której dwie warstwy własne
  // z tego samego serwera dostawały ten sam ID (wtedy ta sama warstwa
  // pojawiała się na liście dwa razy).
  for (const id of order) {
    if (existing.has(id) && !seen.has(id)) {
      out.push(id);
      seen.add(id);
    }
  }
  // Potem items których nie było w order (nowo dodane).
  for (const id of allIds) {
    if (!orderedSet.has(id) && !seen.has(id)) {
      out.push(id);
      seen.add(id);
    }
  }
  return out;
}

function EmbeddedLayerList({
  visibility,
  onChange,
  opacity,
  onOpacityChange,
  customLayers,
  onCustomLayersChange,
  onOpenAddWms,
  visibleLayerIds,
  layerLabels,
  expandedOpacity,
  onToggleOpacityPanel,
  layerOrder,
  onLayerOrderChange,
  onRemoveBuiltinLayer,
  addWmsButtonLabel,
}: EmbeddedLayerListProps) {
  const [draggedId, setDraggedId] = React.useState<string | null>(null);
  const [opacityOpenCustomId, setOpacityOpenCustomId] = React.useState<string | null>(null);

  const availableBuiltin: LayerId[] = LAYERS.filter((l) =>
    !l.parentOf && (!visibleLayerIds || visibleLayerIds.includes(l.id))
  ).map((l) => l.id);
  const customList = customLayers ?? [];
  const renderOrder = resolveRenderOrder(
    availableBuiltin,
    customList.map((l) => l.id),
    layerOrder,
  );

  const toggleBuiltin = (id: LayerId) => {
    onChange({ ...visibility, [id]: !visibility[id] });
  };
  const setBuiltinOpacity = (key: OpacityLayerId, value: number) => {
    if (!opacity || !onOpacityChange) return;
    onOpacityChange({ ...opacity, [key]: value });
  };
  const updateCustom = (
    id: string,
    patch: Partial<import("./AddWmsDialog").CustomWmsLayer>,
  ) => {
    if (!onCustomLayersChange) return;
    onCustomLayersChange(customList.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };
  const removeCustom = (id: string) => {
    if (!onCustomLayersChange) return;
    onCustomLayersChange(customList.filter((l) => l.id !== id));
  };

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDraggedId(null);
    if (!draggedId || draggedId === targetId || !onLayerOrderChange) return;
    const current = [...renderOrder];
    const fromIdx = current.indexOf(draggedId);
    const toIdx = current.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = current.splice(fromIdx, 1);
    current.splice(toIdx, 0, moved);
    onLayerOrderChange(current);
  };

  return (
    <div style={embeddedContainerStyle}>
      <ul style={listStyle}>
        {renderOrder.map((id) => {
          const isCustom = id.startsWith(CUSTOM_LAYER_PREFIX);
          if (isCustom) {
            const customId = id.slice(CUSTOM_LAYER_PREFIX.length);
            const layer = customList.find((l) => l.id === customId);
            if (!layer) return null;
            const opacityOpen = opacityOpenCustomId === customId;
            return (
              <li
                key={id}
                draggable
                onDragStart={(e) => handleDragStart(e, id)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, id)}
                style={{
                  ...itemStyle,
                  opacity: draggedId === id ? 0.5 : 1,
                  background: "#fafafa",
                }}>
                <div style={rowStyle}>
                  <span style={dragHandleStyle} title="Przeciągnij, aby zmienić kolejność">⋮⋮</span>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={layer.visible}
                      onChange={() => updateCustom(layer.id, { visible: !layer.visible })}
                    />
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={layer.title}>
                      {layer.title}
                      <span style={descStyle}> (WMS)</span>
                    </span>
                  </label>
                  {layer.visible && (
                    <button
                      type="button"
                      onClick={() => setOpacityOpenCustomId(opacityOpen ? null : customId)}
                      title={opacityOpen ? "Ukryj suwak" : "Ustaw przezroczystość"}
                      aria-label="Ustaw przezroczystość"
                      style={{
                        ...iconBtnStyle,
                        background: opacityOpen ? "#dbeafe" : "transparent",
                        borderColor: opacityOpen ? "#3b82f6" : "transparent",
                      }}>🎚</button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeCustom(customId)}
                    title="Usuń warstwę"
                    aria-label="Usuń warstwę"
                    style={{ ...iconBtnStyle, color: "#dc2626" }}>✕</button>
                </div>
                {layer.visible && opacityOpen && (
                  <div style={sliderRowStyle}>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={Math.round(layer.opacity * 100)}
                      onChange={(e) => updateCustom(layer.id, { opacity: Number(e.target.value) / 100 })}
                      style={{ flex: 1 }}
                    />
                    <span style={{ color: "#6b7280", minWidth: 32, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {Math.round(layer.opacity * 100)}%
                    </span>
                  </div>
                )}
              </li>
            );
          }
          // Built-in warstwa (top-level, z potencjalnymi dziećmi areas/areaLabels)
          const layerId = id as LayerId;
          const def = LAYERS.find((l) => l.id === layerId);
          if (!def) return null;
          const opacityKey = def.opacityKey;
          const opacityVal = opacityKey && opacity ? opacity[opacityKey] : null;
          const showOpacity = expandedOpacity.has(layerId);
          const canShowOpacity = opacityVal != null && visibility[layerId];
          const displayLabel = layerLabels?.[layerId] ?? def.label;
          // Dzieci (areas/areaLabels pod plan) — widoczne tylko jeśli są w
          // visibleLayerIds i rodzic jest widoczny.
          const children = LAYERS.filter(
            (l) => l.parentOf === layerId
              && (!visibleLayerIds || visibleLayerIds.includes(l.id) || visibleLayerIds.includes(layerId)),
          );
          return (
            <li
              key={id}
              draggable
              onDragStart={(e) => handleDragStart(e, id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, id)}
              style={{ ...itemStyle, opacity: draggedId === id ? 0.5 : 1 }}>
              <div style={rowStyle}>
                <span style={dragHandleStyle} title="Przeciągnij, aby zmienić kolejność">⋮⋮</span>
                <label style={labelStyle}>
                  <input
                    type="checkbox"
                    checked={visibility[layerId]}
                    onChange={() => toggleBuiltin(layerId)}
                  />
                  <span style={{ flex: 1, minWidth: 0 }} title={def.description ? `${displayLabel} — ${def.description}` : displayLabel}>
                    {displayLabel}
                    {def.description && <span style={descStyle}> — {def.description}</span>}
                  </span>
                </label>
                {canShowOpacity && (
                  <button
                    type="button"
                    onClick={() => onToggleOpacityPanel(layerId)}
                    title={showOpacity ? "Ukryj suwak" : "Ustaw przezroczystość"}
                    aria-label="Ustaw przezroczystość"
                    style={{
                      ...iconBtnStyle,
                      background: showOpacity ? "#dbeafe" : "transparent",
                      borderColor: showOpacity ? "#3b82f6" : "transparent",
                    }}>🎚</button>
                )}
                {onRemoveBuiltinLayer && (
                  <button
                    type="button"
                    onClick={() => onRemoveBuiltinLayer(layerId)}
                    title="Usuń z listy warstw (można przywrócić z katalogu 'Warstwy podstawowe')"
                    aria-label="Usuń warstwę z listy"
                    style={{ ...iconBtnStyle, color: "#dc2626" }}>✕</button>
                )}
              </div>
              {canShowOpacity && showOpacity && (
                <div style={sliderRowStyle}>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={Math.round((opacityVal ?? 1) * 100)}
                    onChange={(e) => setBuiltinOpacity(opacityKey!, Number(e.target.value) / 100)}
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: "#6b7280", minWidth: 32, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {Math.round((opacityVal ?? 1) * 100)}%
                  </span>
                </div>
              )}
              {/* Dzieci (areas/areaLabels) — nie drag-able, tylko checkboxy */}
              {children.map((child) => (
                <div key={child.id} style={{ ...rowStyle, paddingLeft: 28, marginTop: 2 }}>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={visibility[child.id]}
                      onChange={() => toggleBuiltin(child.id)}
                    />
                    <span style={{ flex: 1 }}>
                      {layerLabels?.[child.id] ?? child.label}
                    </span>
                  </label>
                </div>
              ))}
            </li>
          );
        })}
      </ul>
      {onOpenAddWms && (
        <div style={{ padding: "6px 10px", borderTop: "1px solid #e5e7eb", background: "#fafafa" }}>
          <button
            type="button"
            onClick={onOpenAddWms}
            style={{ fontSize: 11, padding: "4px 10px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer" }}>
            {addWmsButtonLabel || "+ Dodaj WMS"}
          </button>
        </div>
      )}
    </div>
  );
}

const dragHandleStyle: React.CSSProperties = {
  cursor: "grab",
  color: "#9ca3af",
  fontSize: 16,
  padding: "0 4px",
  userSelect: "none",
  lineHeight: 1,
};

export default LayerControl;
